/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState, useEffect, useCallback } from "react";
import { TextInput, View } from "react-native";
import Icon from "react-icons-kit";
import { exclamation } from "react-icons-kit/metrize/exclamation";

import { useSelector } from "react-redux";
import { RootState } from "store/types";

import { UpduoButton, Text, ButtonType, styles } from "UpduoLibrary";
import {
  skalePrimaryBlue,
  skaleBlack,
  colorWithOpacity,
  upduoPrimaryDark,
} from "config/UpduoColors";

interface OrgCodePaneProps {
  isLoading: boolean;
  onPress: (orgCode: string) => void;
  onSkip: () => void;
  hasError: boolean;
}

export default function OrgCodePane({
  isLoading,
  onPress,
  onSkip,
  hasError,
}: OrgCodePaneProps) {
  const startObject = useSelector((state: RootState) => state.startObject);

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [orgCode, setOrgCode] = useState(startObject?.orgCode || "");
  const [validationText, setValidationText] = useState("");

  useEffect(() => {
    if (!isLoading) {
      if (hasError) {
        setValidationText("Something went wrong!");
      } else if (isSubmitted && !startObject?.orgCode) {
        setValidationText("Community not found");
      }
    }
  }, [isLoading, hasError, isSubmitted, startObject]);

  const _handleButtonPress = useCallback(() => {
    onPress(orgCode);
    setIsSubmitted(true);
  }, [orgCode]);

  return (
    <View
      style={{
        minHeight: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <View
        style={{
          maxWidth: 330,
        }}
      >
        <Text
          style={{
            color: upduoPrimaryDark,
            fontSize: 36,
            lineHeight: 50,
            marginBottom: 24,
            textAlign: "center",
          }}
        >
          {"Do you have a\ncommunity code?"}
        </Text>
        <Text
          style={{
            color: upduoPrimaryDark,
            fontSize: 20,
            lineHeight: 28,
            textAlign: "center",
            marginBottom: 30,
          }}
        >
          {"A community code is provided by your organization or event host."}
        </Text>
        <Text
          style={{
            color: upduoPrimaryDark,
            fontSize: 18,
            marginTop: 30,
          }}
        >
          {"Community Code"}
        </Text>
        <TextInput
          value={orgCode}
          onChangeText={(text: string) => {
            setValidationText("");
            setOrgCode(text);
            setIsSubmitted(false);
          }}
          autoCapitalize={"none"}
          autoCorrect={false}
          style={[styles.textInput, { marginTop: 6 }]}
          autoFocus={true}
        />
        {validationText && !isLoading ? (
          <View
            style={{
              marginTop: 40,
              marginBottom: 30,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <div
              style={{
                color: colorWithOpacity(skalePrimaryBlue, 0.1),
              }}
            >
              <Icon size={50} icon={exclamation} />
            </div>
            <View
              style={{
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Text
                style={{
                  fontWeight: "bold",
                  fontSize: 20,
                  color: skaleBlack,
                }}
              >
                {validationText}
              </Text>
              <Text
                style={{
                  fontSize: 16,
                  color: skaleBlack,
                  textAlign: "center",
                }}
              >
                {"Please try again."}
              </Text>
            </View>
          </View>
        ) : null}
        <UpduoButton
          disabled={!!(validationText || isLoading || !orgCode)}
          onPress={() => _handleButtonPress()}
          text={isLoading ? "Submitting..." : "Submit"}
          style={{
            marginTop: 24,
          }}
        />
        <UpduoButton
          onPress={() => {
            setIsSubmitted(false);
            setValidationText("");
            onSkip();
          }}
          type={ButtonType.LINK}
          style={{ fontSize: 20, lineHeight: 28 } as any}
          text={"SKIP"}
        />
      </View>
    </View>
  );
}
